import React, {useId} from 'react'
import {cn, focusClasses, transitionClasses} from './cn'
import {Reveal} from './Reveal'

type InputVariant = 'default' | 'error' | 'success'
type InputSize = 'small' | 'medium' | 'large'

interface BaseInputProps {
    variant?: InputVariant
    size?: InputSize
    label?: string
    required?: boolean
    helpText?: string
    errorText?: string
    leftIcon?: React.ReactNode
    rightIcon?: React.ReactNode
    suffix?: React.ReactNode
    rightIconInteractive?: boolean
    className?: string
    compact?: boolean
}

export interface InputProps extends BaseInputProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
}

const inputVariants = {
    default: [
        'border-gray-300 text-gray-900 placeholder-gray-500',
        'hover:border-gray-400',
        'focus:border-primary-500 focus:ring-primary-500',
        'dark:border-gray-600 dark:text-white dark:placeholder-gray-400',
        'dark:hover:border-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400'
    ].join(' '),

    error: [
        'border-error-300 text-error-900 placeholder-error-300',
        'hover:border-error-400',
        'focus:border-error-500 focus:ring-error-500',
        'dark:border-error-400 dark:text-error-100 dark:placeholder-error-400',
        'dark:hover:border-error-300 dark:focus:border-error-400 dark:focus:ring-error-400'
    ].join(' '),

    success: [
        'border-success-300 text-success-900 placeholder-success-300',
        'hover:border-success-400',
        'focus:border-success-500 focus:ring-success-500',
        'dark:border-success-400 dark:text-success-100 dark:placeholder-success-400',
        'dark:hover:border-success-300 dark:focus:border-success-400 dark:focus:ring-success-400'
    ].join(' ')
}

const inputSizes = {
    small: 'pl-3 pr-3 py-2 text-[length:max(var(--fitro-editable-font-size,0px),var(--text-body-small))] leading-[var(--lh-body-small)] min-h-[32px] sm:min-h-[32px] min-h-[40px]',
    medium: 'pl-4 pr-4 py-3 text-[length:max(var(--fitro-editable-font-size,0px),var(--text-body))] leading-[var(--lh-body)] min-h-[44px]',
    large: 'pl-5 pr-5 py-4 lg:py-3 text-[length:max(var(--fitro-editable-font-size,0px),var(--text-body-large))] leading-[var(--lh-body-large)] min-h-[52px] lg:min-h-[44px]'
}

const iconSizes = {
    small: 'w-4',
    medium: 'w-5',
    large: 'w-6'
}

const iconPadding = {
    small: {left: 'pl-9', right: 'pr-9'},
    medium: {left: 'pl-11', right: 'pr-11'},
    large: {left: 'pl-13', right: 'pr-13'}
}

const suffixPadding = {
    small: 'pr-12',
    medium: 'pr-14',
    large: 'pr-16'
}

const defaultMax: Record<string, string> = {
    date: '9999-12-31',
    'datetime-local': '9999-12-31T23:59'
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({
         variant = 'default',
         size = 'medium',
         label,
         required = false,
         helpText,
         errorText,
         leftIcon,
         rightIcon,
         suffix,
         rightIconInteractive = false,
         className,
         compact = false,
         id,
         ...props
     }, ref) => {
        const generatedId = useId()
        const inputId = id || generatedId
        const hasError = variant === 'error' || !!errorText
        const actualVariant = hasError ? 'error' : variant

        const baseClasses = [
            'w-full rounded-lg border bg-white dark:bg-gray-800',
            'font-sans font-normal leading-normal',
            transitionClasses,
            focusClasses,
            'focus:ring-1 focus:ring-offset-0',
            'read-only:bg-gray-50 read-only:text-gray-900 read-only:border-gray-200 read-only:cursor-default read-only:hover:border-gray-300',
            'dark:read-only:bg-gray-800/70 dark:read-only:text-gray-100 dark:read-only:border-gray-700 dark:read-only:hover:border-gray-600',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900',
            'placeholder:text-current placeholder:opacity-60'
        ].join(' ')

        const variantClasses = inputVariants[actualVariant]
        const sizeClasses = inputSizes[size]

        let paddingClasses = ''
        if (leftIcon) paddingClasses += ` ${iconPadding[size].left}`
        if (rightIcon) paddingClasses += ` ${iconPadding[size].right}`
        if (suffix) paddingClasses += ` ${suffixPadding[size]}`

        const inputClasses = cn(
            baseClasses,
            variantClasses,
            sizeClasses,
            paddingClasses,
            className
        )

        const labelClasses = cn(
            'block text-xs font-medium',
            compact ? 'mb-0.5' : 'mb-1',
            'text-gray-700 dark:text-gray-200',
            hasError && 'text-error-600 dark:text-error-400'
        )

        const helpTextClasses = cn(
            compact ? 'mt-1 text-caption' : 'mt-2 text-caption',
            hasError ? 'text-error-600 dark:text-error-400' : 'text-gray-500 dark:text-gray-400'
        )

        const getIconClasses = () => cn(
            'absolute top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500',
            hasError && 'text-error-400 dark:text-error-400',
            iconSizes[size]
        )

        return (
            <div className="w-full">
                {label && (
                    <label htmlFor={inputId} className={labelClasses}>
                        {label}
                        {required && (
                            <span className="ml-1 text-error-500" aria-label="required">
                *
              </span>
                        )}
                    </label>
                )}

                <div className="relative">
                    {leftIcon && (
                        <div className={cn(getIconClasses(), 'pointer-events-none left-3')}>
                            {leftIcon}
                        </div>
                    )}

                    <input
                        ref={ref}
                        id={inputId}
                        className={inputClasses}
                        aria-invalid={hasError}
                        aria-describedby={
                            (helpText || errorText) ? `${inputId}-help` : undefined
                        }
                        {...props}
                        // Without max, browsers accept a six-digit year (e.g. 262026).
                        max={props.max ?? defaultMax[props.type ?? '']}
                    />

                    {rightIcon && (
                        <div className={cn(getIconClasses(), !rightIconInteractive && 'pointer-events-none', 'right-3')}>
                            {rightIcon}
                        </div>
                    )}

                    {suffix && (
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-gray-500 dark:text-gray-400">
                            {suffix}
                        </div>
                    )}
                </div>

                <Reveal show={Boolean(helpText || errorText)}>
                    {(helpText || errorText) && (
                        <div id={`${inputId}-help`} className={helpTextClasses}>
                            {errorText || helpText}
                        </div>
                    )}
                </Reveal>
            </div>
        )
    }
)

Input.displayName = 'Input'

interface TextareaProps extends BaseInputProps, Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({
         variant = 'default',
         size = 'medium',
         label,
         required = false,
         helpText,
         errorText,
         className,
         compact = false,
         id,
         rows = 4,
         ...props
     }, ref) => {
        const generatedId = useId()
        const textareaId = id || generatedId
        const hasError = variant === 'error' || !!errorText
        const actualVariant = hasError ? 'error' : variant

        const baseClasses = [
            'w-full rounded-lg border bg-white dark:bg-gray-800',
            'font-sans font-normal leading-normal resize-y',
            transitionClasses,
            focusClasses,
            'focus:ring-1 focus:ring-offset-0',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900',
            'placeholder:text-current placeholder:opacity-60'
        ].join(' ')

        const variantClasses = inputVariants[actualVariant]
        const sizeClasses = inputSizes[size]

        const textareaClasses = cn(
            baseClasses,
            variantClasses,
            sizeClasses,
            className
        )

        const labelClasses = cn(
            'block text-xs font-medium',
            compact ? 'mb-0.5' : 'mb-1',
            'text-gray-700 dark:text-gray-200',
            hasError && 'text-error-600 dark:text-error-400'
        )

        const helpTextClasses = cn(
            compact ? 'mt-1 text-caption' : 'mt-2 text-caption',
            hasError ? 'text-error-600 dark:text-error-400' : 'text-gray-500 dark:text-gray-400'
        )

        return (
            <div className="w-full">
                {label && (
                    <label htmlFor={textareaId} className={labelClasses}>
                        {label}
                        {required && (
                            <span className="ml-1 text-error-500" aria-label="required">
                *
              </span>
                        )}
                    </label>
                )}

                <textarea
                    ref={ref}
                    id={textareaId}
                    rows={rows}
                    className={textareaClasses}
                    aria-invalid={hasError}
                    aria-describedby={
                        (helpText || errorText) ? `${textareaId}-help` : undefined
                    }
                    {...props}
                />

                <Reveal show={Boolean(helpText || errorText)}>
                    {(helpText || errorText) && (
                        <div id={`${textareaId}-help`} className={helpTextClasses}>
                            {errorText || helpText}
                        </div>
                    )}
                </Reveal>
            </div>
        )
    }
)

Textarea.displayName = 'Textarea'

interface SelectProps extends BaseInputProps, Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
    options?: Array<{ value: string; label: string; disabled?: boolean }>
    placeholder?: string
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
    ({
         variant = 'default',
         size = 'medium',
         label,
         required = false,
         helpText,
         errorText,
         options = [],
         placeholder,
         className,
         compact = false,
         id,
         children,
         ...props
     }, ref) => {
        const generatedId = useId()
        const selectId = id || generatedId
        const hasError = variant === 'error' || !!errorText
        const actualVariant = hasError ? 'error' : variant

        const baseClasses = [
            'w-full rounded-lg border bg-white dark:bg-gray-800',
            'font-sans font-normal leading-normal',
            'appearance-none cursor-pointer',
            'bg-[url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")]',
            'bg-no-repeat bg-right-3 bg-[length:1.25rem_1.25rem]',
            transitionClasses,
            focusClasses,
            'focus:ring-1 focus:ring-offset-0',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900'
        ].join(' ')

        const variantClasses = inputVariants[actualVariant]
        const sizeClasses = inputSizes[size]

        // pr-10 after the size so long options stay clear of the chevron.
        const selectClasses = cn(
            baseClasses,
            variantClasses,
            sizeClasses,
            'pr-10',
            className
        )

        const labelClasses = cn(
            'block text-xs font-medium',
            compact ? 'mb-0.5' : 'mb-1',
            'text-gray-700 dark:text-gray-200',
            hasError && 'text-error-600 dark:text-error-400'
        )

        const helpTextClasses = cn(
            compact ? 'mt-1 text-caption' : 'mt-2 text-caption',
            hasError ? 'text-error-600 dark:text-error-400' : 'text-gray-500 dark:text-gray-400'
        )

        return (
            <div className="w-full">
                {label && (
                    <label htmlFor={selectId} className={labelClasses}>
                        {label}
                        {required && (
                            <span className="ml-1 text-error-500" aria-label="required">
                *
              </span>
                        )}
                    </label>
                )}

                <select
                    ref={ref}
                    id={selectId}
                    className={selectClasses}
                    aria-invalid={hasError}
                    aria-describedby={
                        (helpText || errorText) ? `${selectId}-help` : undefined
                    }
                    {...props}
                >
                    {placeholder && (
                        <option value="" disabled>
                            {placeholder}
                        </option>
                    )}
                    {options.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                        >
                            {option.label}
                        </option>
                    ))}
                    {children}
                </select>

                <Reveal show={Boolean(helpText || errorText)}>
                    {(helpText || errorText) && (
                        <div id={`${selectId}-help`} className={helpTextClasses}>
                            {errorText || helpText}
                        </div>
                    )}
                </Reveal>
            </div>
        )
    }
)

Select.displayName = 'Select'
